import React, { useState, useRef, useEffect } from 'react';
import './AIChat.css';
import { analyzeColors } from '../../services/colorAnalysis';

const AIChat = ({ onApplyChanges, onClose }) => {
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: 'Merhaba! Size tasarım konusunda nasıl yardımcı olabilirim? Örneğin:\n- "Butonları yeşil yap"\n- "Header\'ı koyu mavi yap"\n- "Arkaplanı mor yap"\n- "Temayı tamamen değiştir"'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    setMessages(prev => [...prev, { type: 'user', content: input }]);
    setIsLoading(true);

    try {
      const response = await fetch(
        "https://api-inference.huggingface.co/models/mistralai/Mistral-7B-Instruct-v0.3",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer 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",
          },
          body: JSON.stringify({
            inputs: `<s>[INST] Sen deneyimli bir UI/UX tasarımcısısın. Modern web tasarımı konusunda uzmansın ve renk teorisi bilgin çok iyi. 

RENK DEĞİŞİKLİK KURALLARI:

1. SADECE ÇARK RENKLERİ İSTENDİĞİNDE:
   - SADECE şu alanları değiştir: backgroundColor1, backgroundColor2, textColor1, textColor2, borderColor, pointerColor, radiusLineColor
   - backgroundColor1 ve backgroundColor2 birbirini tamamlayan renkler olmalı
   - DİĞER TÜM RENKLERE DOKUNMA

2. SADECE HEADER İSTENDİĞİNDE:
   - SADECE şu alanları değiştir: headerBg, headerIconColor
   - DİĞER TÜM RENKLERE DOKUNMA

3. SADECE BUTON RENKLERİ İSTENDİĞİNDE:
   - SADECE şu alanları değiştir: buttonGradient1, buttonGradient2, buttonTextColor, buttonBorderColor, buttonGlowColor
   - DİĞER TÜM RENKLERE DOKUNMA

4. SADECE ARKAPLAN İSTENDİĞİNDE:
   - SADECE şu alanları değiştir: backgroundGradient1, backgroundGradient2
   - DİĞER TÜM RENKLERE DOKUNMA

RENK FORMATLARI:
- HEX renk kodları kullan (#RRGGBB formatında)
- Header için rgba(R,G,B,0.7) formatını kullan
- Örnek HEX kodları: #FF0000 (kırmızı), #00FF00 (yeşil), #0000FF (mavi)
- Örnek rgba: rgba(255,0,0,0.7) (kırmızı), rgba(0,255,0,0.7) (yeşil)

ÖRNEK YANIT:
1. Türkçe açıklama: Arka plan renklerini modern ve koyu tonlarda güncelliyorum.
2. JSON renk şeması:
\`\`\`json
{
  "backgroundGradient1": "#1A1A1A",
  "backgroundGradient2": "#333333"
}
\`\`\`

RENK UYUMU KURALLARI:
- Ana renk + Tamamlayıcı renk kullan
- Koyu arka planlar için canlı vurgular
- Gradyanlar için ton farklılıkları

ÖNEMLİ: 
- SADECE değiştirilecek renkleri JSON'a ekle
- HEX kodlarını doğru formatta kullan (#RRGGBB)
- Header için rgba formatını kullan

Kullanıcı isteği: ${input} [/INST]</s>`,
            parameters: {
              max_new_tokens: 1000,
              temperature: 0.7,
              return_full_text: false,
            },
          }),
        }
      );

      const result = await response.json();
      let aiResponse = result[0].generated_text;

      // JSON'u bul ve parse et
      const jsonMatch = aiResponse.match(/```json\n([\s\S]*?)\n```/);
      if (jsonMatch) {
        try {
          const changes = JSON.parse(jsonMatch[1]);
          
          // Renk formatlarını kontrol et
          Object.entries(changes).forEach(([key, value]) => {
            if (key === 'headerBg') {
              if (!isValidRgba(value)) {
                throw new Error('Geçersiz rgba formatı');
              }
            } else {
              if (!isValidHexColor(value)) {
                throw new Error('Geçersiz HEX renk kodu');
              }
            }
          });

          // İsteğe göre izin verilen alanları belirle
          const allowedFields = {
            'çark': ['backgroundColor1', 'backgroundColor2', 'textColor1', 'textColor2', 'borderColor', 'pointerColor', 'radiusLineColor'],
            'header': ['headerBg', 'headerIconColor'],
            'buton': ['buttonGradient1', 'buttonGradient2', 'buttonTextColor', 'buttonBorderColor', 'buttonGlowColor'],
            'arkaplan': ['backgroundGradient1', 'backgroundGradient2']
          };

          // Kullanıcı isteğini analiz et
          const isWheelOnly = input.toLowerCase().includes('çark') || input.toLowerCase().includes('wheel');
          const isHeaderOnly = input.toLowerCase().includes('header') || input.toLowerCase().includes('başlık');
          const isButtonOnly = input.toLowerCase().includes('buton') || input.toLowerCase().includes('button');
          const isBackgroundOnly = input.toLowerCase().includes('arkaplan') || input.toLowerCase().includes('background');
          
          // Genel tema değişikliği kontrolü
          const isGeneralTheme = input.toLowerCase().includes('tema') || 
                                input.toLowerCase().includes('theme') ||
                                !isWheelOnly && !isHeaderOnly && !isButtonOnly && !isBackgroundOnly;

          // Sadece spesifik değişiklik isteniyorsa filtrele
          if (!isGeneralTheme) {
            const allowedKeys = [
              ...(isWheelOnly ? allowedFields.çark : []),
              ...(isHeaderOnly ? allowedFields.header : []),
              ...(isButtonOnly ? allowedFields.buton : []),
              ...(isBackgroundOnly ? allowedFields.arkaplan : [])
            ];

            // İzin verilmeyen tüm alanları sil
            Object.keys(changes).forEach(key => {
              if (!allowedKeys.includes(key)) {
                delete changes[key];
              }
            });

            // Değişiklik yapılacak en az bir alan kaldı mı kontrol et
            if (Object.keys(changes).length === 0) {
              throw new Error('Geçerli değişiklik bulunamadı');
            }
          }

          // Mesajı JSON olmadan göster
          const message = aiResponse.replace(/```json\n[\s\S]*?\n```/, '').trim();
          setMessages(prev => [...prev, { type: 'ai', content: message }]);
          
          // Değişiklikleri uygula
          onApplyChanges(changes);
        } catch (jsonError) {
          console.error('JSON Parse Error:', jsonError);
          setMessages(prev => [...prev, { 
            type: 'ai', 
            content: 'Üzgünüm, renk şemasını işlerken bir hata oluştu. Lütfen "sadece buton renklerini kırmızı yap" gibi daha net bir ifade kullanın.' 
          }]);
        }
      } else {
        setMessages(prev => [...prev, { 
          type: 'ai', 
          content: 'Üzgünüm, geçerli bir renk şeması bulunamadı. Lütfen isteğinizi "sadece çark renklerini değiştir" gibi daha net ifade edin.' 
        }]);
      }

    } catch (error) {
      console.error('Hugging Face API Error:', error);
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: 'Üzgünüm, bir hata oluştu. Lütfen tekrar deneyin.' 
      }]);
    }

    setIsLoading(false);
    setInput('');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'Lütfen geçerli bir görsel dosyası yükleyin (jpg, png, gif, vb.)'
      }]);
      return;
    }

    setMessages(prev => [...prev, {
      type: 'user',
      content: 'Görsel yüklendi: ' + file.name,
      isImage: true
    }]);

    setIsLoading(true);
    try {
      const colors = await analyzeColors(file);
      
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'Görseldeki renkleri analiz ettim ve çark tasarımına uyguladım! Ana tema aynı kalacak şekilde çark renkleri güncellendi.'
      }]);

      onApplyChanges(colors);
    } catch (error) {
      console.error('Renk analizi hatası:', error);
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'Görsel analizi sırasında bir hata oluştu.'
      }]);
    }
    setIsLoading(false);
  };

  // Renk formatı kontrolü ekle
  const isValidHexColor = (color) => /^#[0-9A-F]{6}$/i.test(color);
  const isValidRgba = (color) => /^rgba\(\d{1,3},\s*\d{1,3},\s*\d{1,3},\s*0?\.[0-9]+\)$/.test(color);

  return (
    <div className="ai-chat">
      <div className="chat-header">
        <div className="chat-header-left">
          <span className="ai-icon">🤖</span>
          <h3>Hook Chat</h3>
          <span className="beta-tag">Beta</span>
        </div>
        <button className="close-chat" onClick={onClose}>×</button>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.type}`}>
            <span className="message-icon">
              {msg.type === 'ai' ? '🤖' : msg.isImage ? '🖼️' : '👤'}
            </span>
            <div className="message-content">
              {msg.content.split('\n').map((line, i) => (
                <p key={i}>{line}</p>
              ))}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="message ai">
            <span className="message-icon">🤖</span>
            <div className="message-content">
              <p>Düşünüyorum...</p>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      <div className="chat-input">
        <div className="input-actions">
          <button 
            className="upload-image"
            onClick={() => fileInputRef.current?.click()}
            title="Görsel Yükle"
          >
            🖼️
          </button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Tasarım değişikliği için bir şey yazın..."
          rows="1"
        />
        <button 
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
        >
          <span>📤</span>
        </button>
      </div>
    </div>
  );
};

export default AIChat; 
